// Drag and drop, as a click drawn out (`carry.ts`).
//
// **It arms and it clicks, and owns no meaning of its own.** Picking a tile up
// is arming it, which lights the board exactly as the click does; letting go
// on a lit square is `pickSquare` on it. So every refusal, every command and
// every rule a drop obeys is the click flow's, said once.

import type { CarryPoint, CarrySpec, Square } from "src/api";
import type { PieceType } from "src/engine/rules/board";
import type { StructureKind } from "src/engine/rules/industry/state";

import { type CarryInputs, carrySpec } from "./carry";
import type { SessionCore } from "./core";
import { selectedMoves } from "./marks";

/**
 * What the carry needs from the verbs it does not own: arming is the hotbar
 * module's, and the drop is the Session's own click.
 *
 * Generic over the tiles, because a tile is the hotbar's shape and the carry
 * reads one field of each — `type` of a forge, `kind` of a build.
 */
export interface CarryDeps<F extends { type: PieceType }, B extends { kind: StructureKind }> {
  armForge(tile: F): void;
  armBuild(tile: B): void;
  beginPlace(kind: StructureKind): void;
  pickSquare(square: number): void;
}

export function carryVerbs<F extends { type: PieceType }, B extends { kind: StructureKind }>(
  core: SessionCore,
  deps: CarryDeps<F, B>
) {
  /** Everything a carry's targets are read off — the click flow's own state. */
  const inputs = (): CarryInputs => ({
    selected: core.selected,
    movesFromSelected: selectedMoves(
      core.moves,
      core.selected,
      core.controlsTurn,
      core.localSide,
      core.state.position
    ),
    forging: core.forging,
    placing: core.placing,
    localSide: core.localSide,
    mintFacing: core.mintFacing
  });

  const spec = (): CarrySpec | undefined => carrySpec(core.carry?.what, inputs());

  /**
   * May a drag from this square pick its piece up?
   *
   * **Only the piece already selected, and only while it has somewhere to
   * go.** A drag is how the board is looked around, from anywhere, and a first
   * press that could lift a piece would take that away over every army — so
   * the select is what says "this drag is about the piece".
   */
  const canCarry = (square: number): boolean =>
    square === core.selected &&
    inputs().movesFromSelected.length > 0 &&
    !core.pending &&
    !core.aiming &&
    !core.forging &&
    !core.placing;

  const carryPiece = (square: number, at: CarryPoint): void => {
    if (!canCarry(square)) return;
    core.carry = { what: { kind: "piece", from: square }, at };
    core.changed();
  };

  // **Armed only if it is not already**: arming is a toggle, and a drag off a
  // lit tile must not put it down. A tile that refuses says so itself and
  // arms nothing, which leaves `spec` undefined and the drag empty-handed.
  const carryForge = (tile: F, at: CarryPoint): void => {
    if (core.forging?.type !== tile.type) deps.armForge(tile);
    core.carry = { what: { kind: "forge", type: tile.type }, at };
    core.changed();
  };

  const carryBuild = (tile: B, at: CarryPoint): void => {
    if (core.forging?.build !== tile.kind) deps.armBuild(tile);
    core.carry = { what: { kind: "build", structure: tile.kind }, at };
    core.changed();
  };

  const carryToken = (kind: StructureKind, at: CarryPoint): void => {
    if (core.placing?.kind !== kind) deps.beginPlace(kind);
    core.carry = { what: { kind: "token", structure: kind }, at };
    core.changed();
  };

  /**
   * The pointer came up. `square` is the lit square under it, or negative.
   *
   * **Let go of nowhere, a tool goes back on the bar and a piece goes back on
   * its square still selected** — the two things "never mind" already means
   * for each, so a drop that misses costs nothing a click would not.
   */
  const dropCarry = (square: number): void => {
    const what = core.carry?.what;
    if (!what) return;
    // The targets are the carry's, so they are read before it is let go of.
    const targets = spec()?.targets;
    core.carry = undefined;
    if (square >= 0 && targets?.includes(square as Square)) {
      deps.pickSquare(square);
      core.changed();
      return;
    }
    if (what.kind === "token") core.placing = undefined;
    else if (what.kind !== "piece") core.forging = undefined;
    core.changed();
  };

  return {
    /** Set from the press to the release, whether or not anything is in hand. */
    carrying: (): boolean => core.carry !== undefined,
    /** What the board draws in the hand, and where the drag began. */
    carry: (): { spec: CarrySpec; at: CarryPoint } | undefined => {
      const drawn = spec();
      return drawn && core.carry ? { spec: drawn, at: core.carry.at } : undefined;
    },
    canCarry,
    carryPiece,
    carryForge,
    carryBuild,
    carryToken,
    dropCarry
  };
}

export type CarryVerbs<F extends { type: PieceType }, B extends { kind: StructureKind }> = ReturnType<
  typeof carryVerbs<F, B>
>;
